import React from "react";
import { useHistory } from "react-router-dom";
import { Api } from "../../api";
import {
  makeStyles,
  Drawer,
  IconButton,
  useTheme,
  Divider,
  AppBar,
  Toolbar,
  Typography,
} from "@material-ui/core";
import TrainingList from "./TrainingList";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import MenuIcon from "@material-ui/icons/Menu";
import { TrainingTopic } from "../../types/TrainingTopic";
import clsx from "clsx";
import TrainingPlayer from "./TrainingPlayer";
import {
  TrainingTopicStatus,
  TrainingAssessmentStatus,
} from "../../types/TrainingTopicStatus";
import { hasError } from "../../helpers/ResponseHelper";

interface IProps {
  preview?: Boolean;
  trainingId: string;
  trainingVersion: string;
  onTopicDone?: (
    topic: TrainingTopic,
    assessmentStatus?: TrainingAssessmentStatus
  ) => void;
  onUpdateTopic?: (
    topic: TrainingTopic,
    assessmentStatus?: TrainingAssessmentStatus
  ) => void;
  updateVideoStatus?: (topic: TrainingTopic, time: number) => void;
  status?: TrainingTopicStatus[];
  onTrainingDone?: () => void;
}

export default function Training(props: IProps) {
  const {
    preview,
    trainingId,
    trainingVersion,
    onTopicDone,
    onUpdateTopic,
    status,
    updateVideoStatus,
    onTrainingDone,
  } = props;
  const [training, setTraining] = React.useState();
  const [topics, setTopics] = React.useState(Array<TrainingTopic>());
  const [flattenedTopics, setFlattenedTopics] = React.useState(
    Array<TrainingTopic>()
  );
  const classes = useStyles();
  const [drawerOpen, setDrawerOpen] = React.useState(true);
  const theme = useTheme();
  const [selectedTopic, setSelectedTopic] = React.useState({} as TrainingTopic);
  const [trainingDone, setTrainingDone] = React.useState(false);
  const history = useHistory();

  const getTraining = React.useCallback(() => {
    if (trainingId && trainingVersion) {
      Api.training
        .getTopicsByTrainingId(trainingId, trainingVersion)
        .then((result) => {
          if (!hasError(result)) {
            let training = result.data.data;
            setTraining(training);
          }
        });
    }
  }, [trainingId, trainingVersion]);

  // flatten topics
  React.useEffect(() => {
    if (training) {
      let topics = training.topics;
      let a = Array<TrainingTopic>();
      if (topics) {
        for (const topic of topics) {
          if (topic.active) {
            if (!topic.group) {
              if (status) topic.status = status[topic.trainingTopicId];
              a.push(topic);
            } else {
              if (topic.subTopics && topic.subTopics.length > 0) {
                for (const subTopic of topic.subTopics) {
                  a.push(subTopic);
                }
              }
            }
          }
        }
        setTopics(topics);
        setFlattenedTopics(a);
        let done = true;
        if (a.length > 0) {
          for (let t of a) {
            if (!t.status || !t.status.complete) {
              setSelectedTopic(t);
              done = false;
              break;
            }
          }
        }
        setTrainingDone(done);
      }
    }
  }, [training, status]);

  React.useEffect(() => {
    if (trainingDone) {
      onTrainingDone?.();
    }
  }, [trainingDone, onTrainingDone]);

  React.useEffect(() => {
    getTraining();
  }, [getTraining]);

  function onTopicClickHandler(topic: TrainingTopic) {
    if (preview && !topic.group) setSelectedTopic(topic);
  }

  function nextTopicHandler(score?: number) {
    if (selectedTopic.contentType === "ASSESSMENT") {
      markTopicDone(selectedTopic, {
        score,
        questions: selectedTopic.assessment?.questions,
      } as TrainingAssessmentStatus);
    } else {
      markTopicDone(selectedTopic);
    }
    console.log(selectedTopic);
    let done = true;
    for (let i = 0; i < flattenedTopics.length; i++) {
      let topic = flattenedTopics[i];
      if (topic.trainingTopicId === selectedTopic.trainingTopicId) {
        let nextTopicIndex = i + 1;
        if (nextTopicIndex < flattenedTopics.length) {
          setSelectedTopic(flattenedTopics[nextTopicIndex]);
          done = false;
        }
      }
    }
    setTrainingDone(done);
  }

  function updateTopic(score?: number) {
    if (selectedTopic.contentType === "ASSESSMENT") {
      updateTopicDone(selectedTopic, {
        score,
        questions: selectedTopic.assessment?.questions,
      } as TrainingAssessmentStatus);
    } else {
      updateTopicDone(selectedTopic);
    }
  }

  function updateTopicDone(
    topic: TrainingTopic,
    status?: TrainingAssessmentStatus
  ) {
    if (onUpdateTopic) onUpdateTopic(topic, status);
  }

  function markTopicDone(
    topic: TrainingTopic,
    status?: TrainingAssessmentStatus
  ) {
    if (onTopicDone) onTopicDone(topic, status);
  }

  return (
    <div style={{ display: "flex" }} className={preview ? "preview" : ""}>
      <AppBar
        position="fixed"
        color="secondary"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: drawerOpen,
        })}
      >
        <Toolbar>
          <IconButton
            edge="start"
            className={clsx(classes.menuButton)}
            color="inherit"
            aria-label="menu"
            onClick={() => setDrawerOpen(!drawerOpen)}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" className={classes.title}>
            {training && training.trainingTitle}
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="left"
        classes={{
          paper: classes.drawerPaper,
        }}
        open={drawerOpen}
      >
        <div className={classes.sidebar}>
          <div className={classes.drawerHeader}>
            <IconButton onClick={() => history.goBack()}>
              {theme.direction === "ltr" ? (
                <ChevronLeftIcon className={classes.drawerButton} />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </div>
          <Divider />
          {training ? (
            <div style={{ padding: "0 30px 30px 30px" }}>
              <TrainingList
                topics={topics}
                isSubList={false}
                selectedTopicId={selectedTopic.trainingTopicId || ""}
                onTopicClick={onTopicClickHandler}
              />
            </div>
          ) : null}
        </div>
      </Drawer>
      <main
        className={clsx(classes.content, {
          [classes.contentShift]: drawerOpen,
        })}
      >
        <TrainingPlayer
          topic={selectedTopic}
          nextTopic={nextTopicHandler}
          updateTopic={updateTopic}
          submitAssessment={(score: number) => nextTopicHandler(score)}
          updateVideoStatus={(time: number) =>
            updateVideoStatus?.(selectedTopic, time)
          }
        />
      </main>
    </div>
  );
}

const drawerWidth = 300;

const useStyles = makeStyles((theme) => ({
  appBar: {
    zIndex: 10000,
    color: "white",
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerButton: {
    color: "#fff",
  },
  offset: {
    ...theme.mixins.toolbar,
  },
  drawer: {
    zIndex: 9999,
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    border: "none",
  },
  sidebar: {
    background: theme.palette.primary.main,
    color: "#fff",
    minHeight: "100vh",
    overflow: "auto",
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
  },
  content: {
    flexGrow: 1,
    display: "flex",
    flexFlow: "column",
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
    minHeight: "calc(100vh - 64px)",
    background: "#f5f5f5",
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
  menuButton: {
    marginRight: theme.spacing(2),
    color: "white",
  },
  hide: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    color: "white",
  },
}));
